// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: messages the Assistant prepares during voice mode. It still opens its usual
// review card; voice mode reads the card's draft and, for a Konvo chat, sends it after a
// short countdown (Settings → Assistant, 5 seconds by default) unless the person says
// "wait", edits it or cancels. Mail, WhatsApp and Teams always wait for "send it" (or
// the Send button). Sending is the card's own Confirm, so the server path, audit and
// usage records stay exactly as they are for a tapped Confirm.
//
// Coding-agent cards (design/agents-side-bar r2-v2, r2-v3) never go on their own: Konvo reads back
// what goes where, and a spoken "yes" (or the card's own verb, "allow it"), the button under the
// words, or the card in the chat confirms it. "Change it" keeps it waiting while the person says
// what to change; the Assistant's new card replaces it.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import AssistantSpeech from '@app/features/konvo_assistant/state/AssistantSpeech';
import {http} from '@app/features/platform/transport/RestTransport';
import {makeAutoObservable, runInAction} from 'mobx';

/** Spoken while a prepared message is waiting (the server recognises these; see draftCommandFor). */
export type VoiceDraftCommand = 'send' | 'wait' | 'cancel';

export type VoiceDraftPhase = 'counting' | 'held' | 'sending' | 'sent' | 'cancelled' | 'failed';

/** What confirming an agent card does; the server matches "yes" and this verb (agentCardCommandFor). */
export type AgentCardVerb =
	| 'send'
	| 'start'
	| 'stop'
	| 'allow'
	| 'deny'
	| 'answer'
	| 'settle'
	| 'unsettle'
	| 'rename'
	| 'merge'
	| 'ask'
	/** Ask a colleague the agent's question (agent_ask). */
	| 'askPerson'
	/** Close the agent's question without an answer (agent_dismiss). */
	| 'dismiss';

export interface VoiceAgentDraft {
	kind: string;
	verb: AgentCardVerb;
	machineId: string;
	/** The thread it is about; null for a new chat (and a merge from someone's ask). */
	threadId: string | null;
	/** A new chat's project. */
	projectTitle: string;
}

export interface VoiceDraft {
	cardId: string;
	/** A Konvo chat message: the only kind that may go out on its own. */
	internal: boolean;
	/** The Konvo channel it goes to, when known, so it can be shown in that chat's composer. */
	channelId: string | null;
	/** Who or where it goes, as the card names it ("Nuvio ManCo"). */
	label: string;
	text: string;
	phase: VoiceDraftPhase;
	/** How long it counts down once Konvo has finished speaking. */
	delaySeconds: number;
	/** When a counting draft goes out (ms since epoch); null until the countdown starts. */
	sendsAt: number | null;
	/** A coding-agent card: waits for a yes, never counts down. */
	agent?: VoiceAgentDraft;
	/** The card's own words after Confirm: what happened, or why nothing did. */
	note?: string;
	/** The person tapped Change it: Konvo listens for what to change. */
	changing?: boolean;
}

interface CardDraftResponse {
	draft?: {
		kind?: string;
		destinationId?: string;
		text?: string;
		subject?: string;
		to?: Array<string>;
		// Agent cards (AssistantActionDraft agent_*).
		machineId?: string;
		threadId?: string;
		threadTitle?: string;
		projectTitle?: string;
		prompt?: string;
		decision?: string;
		detail?: string;
		lines?: Array<{question: string; answer: string}>;
		settled?: boolean;
		title?: string;
		number?: number;
		pullTitle?: string;
		ask?: boolean;
		personName?: string;
		questions?: Array<string>;
	};
	done?: boolean;
	expired?: boolean;
	options?: Array<{id: string; name: string}>;
}

/** How long a sent or cancelled draft stays on screen as a step before it clears. */
const SETTLED_FOR_MS = 6000;
/** An agent card's outcome is a sentence to read: it stays a little longer. */
const SETTLED_AGENT_FOR_MS = 12_000;

function agentVerb(draft: NonNullable<CardDraftResponse['draft']>): AgentCardVerb | null {
	switch (draft.kind) {
		case 'agent_prompt':
			return 'send';
		case 'agent_start':
			return 'start';
		case 'agent_stop':
			return 'stop';
		case 'agent_approval':
			return draft.decision === 'decline' || draft.decision === 'cancel' ? 'deny' : 'allow';
		case 'agent_answer':
			return 'answer';
		case 'agent_settle':
			return draft.settled === false ? 'unsettle' : 'settle';
		case 'agent_rename':
			return 'rename';
		case 'agent_merge':
			return draft.ask ? 'ask' : 'merge';
		case 'agent_ask':
			return 'askPerson';
		case 'agent_dismiss':
			return 'dismiss';
		default:
			return null;
	}
}

/** A coding-agent card: where it goes (the thread, a new chat's project, the pull request) and what it says. */
export function agentDestinationOf(
	response: CardDraftResponse,
): Pick<VoiceDraft, 'internal' | 'channelId' | 'label' | 'text' | 'agent'> | null {
	const draft = response.draft;
	const verb = draft ? agentVerb(draft) : null;
	if (!draft || !verb) return null;
	const merge = draft.kind === 'agent_merge';
	const label = merge
		? `#${draft.number ?? ''} ${draft.pullTitle ?? ''}`.trim()
		: draft.kind === 'agent_start'
			? ((draft.title?.trim() || draft.projectTitle) ?? '')
			: (draft.threadTitle ?? '');
	const text =
		draft.kind === 'agent_prompt' || draft.kind === 'agent_start'
			? (draft.prompt ?? '')
			: draft.kind === 'agent_approval'
				? (draft.detail ?? '')
				: draft.kind === 'agent_answer'
					? (draft.lines ?? []).map((line) => line.answer).join(' · ')
					: draft.kind === 'agent_rename'
						? (draft.title ?? '')
						: draft.kind === 'agent_ask' || draft.kind === 'agent_dismiss'
							? (draft.questions?.[0] ?? '')
							: '';
	return {
		internal: false,
		channelId: null,
		label,
		text,
		agent: {
			kind: draft.kind!,
			verb,
			machineId: draft.machineId ?? '',
			threadId: draft.threadId || null,
			projectTitle: draft.projectTitle ?? '',
		},
	};
}

/** Two agent cards about the same thing (a new one replaces the old: "change it to …"). */
function sameAgentTarget(a: VoiceAgentDraft | undefined, b: VoiceAgentDraft | undefined): boolean {
	if (!a || !b) return false;
	if (a.threadId || b.threadId) return a.machineId === b.machineId && a.threadId === b.threadId;
	return a.kind === b.kind && a.machineId === b.machineId && a.projectTitle === b.projectTitle;
}

function destinationOf(
	response: CardDraftResponse,
): Pick<VoiceDraft, 'internal' | 'channelId' | 'label' | 'text' | 'agent'> | null {
	const draft = response.draft;
	if (!draft?.kind) return null;
	if (draft.kind.startsWith('agent_')) return agentDestinationOf(response);
	if (draft.kind === 'message' && draft.destinationId) {
		const option = response.options?.find((candidate) => candidate.id === draft.destinationId);
		const label = option?.name.split(' · ')[0] ?? '';
		const konvo = draft.destinationId.startsWith('konvo:') || draft.destinationId.startsWith('konvo-user:');
		const channelId = draft.destinationId.startsWith('konvo:') ? draft.destinationId.slice(6) : null;
		return {
			internal: konvo,
			channelId: channelId && /^\d+$/.test(channelId) ? channelId : null,
			label,
			text: draft.text ?? '',
		};
	}
	if (draft.kind === 'mail') {
		return {internal: false, channelId: null, label: draft.to?.[0] ?? draft.subject ?? '', text: draft.subject ?? ''};
	}
	if (draft.kind === 'whatsapp') return {internal: false, channelId: null, label: 'WhatsApp', text: draft.text ?? ''};
	return null;
}

export class VoiceModeDraftsStore {
	current: VoiceDraft | null = null;
	private seen = new Set<string>();
	private timer: ReturnType<typeof setTimeout> | null = null;
	private clearTimer: ReturnType<typeof setTimeout> | null = null;
	/** Konvo is not speaking or working, so a countdown can run (and "wait" can be heard). */
	private quiet = false;

	constructor() {
		makeAutoObservable<this, 'seen' | 'timer' | 'clearTimer' | 'quiet'>(
			this,
			{seen: false, timer: false, clearTimer: false, quiet: false},
			{autoBind: true},
		);
	}

	/** A draft is waiting for the person, so "send it", "wait" and "cancel" are about it. */
	get awaiting(): boolean {
		return this.current?.phase === 'counting' || this.current?.phase === 'held';
	}

	/** What the server needs to hear the answer to the waiting draft (X-Konvo-Voice-Draft), or null. */
	get voiceHeader(): string | null {
		if (!this.awaiting) return null;
		return this.current?.agent ? `agent:${this.current.agent.verb}` : '1';
	}

	/** Change it: the card keeps waiting while the person says what to change. */
	markChanging(): void {
		const draft = this.current;
		if (draft && (draft.phase === 'held' || draft.phase === 'counting')) {
			this.hold();
			draft.changing = true;
		}
	}

	/** New review cards on the latest answer: load each once and decide what happens to it. */
	offer(cardIds: ReadonlyArray<string>): void {
		for (const cardId of cardIds) {
			if (this.seen.has(cardId)) continue;
			this.seen.add(cardId);
			void this.load(cardId);
		}
	}

	private async load(cardId: string): Promise<void> {
		let response: CardDraftResponse | null = null;
		try {
			response = (await http.get<CardDraftResponse>(Endpoints.KONVO_CARD_DRAFT(cardId))).body ?? null;
		} catch {
			return;
		}
		if (!response || response.done || response.expired) return;
		const destination = destinationOf(response);
		if (!destination) return;
		runInAction(() => {
			const previous = this.current;
			// "Change it to …": the Assistant's new card for the same thread takes the old one's place.
			if (
				previous &&
				previous.cardId !== cardId &&
				(previous.phase === 'held' || previous.phase === 'counting') &&
				sameAgentTarget(previous.agent, destination.agent)
			)
				void http.post(Endpoints.KONVO_CARD_ACTION(previous.cardId, 'cancel'), {body: {}}).catch(() => undefined);
			this.clearTimers();
			const delay = destination.internal ? AssistantSpeech.voiceSendDelay : 0;
			this.current = {
				cardId,
				...destination,
				phase: delay > 0 ? 'counting' : 'held',
				delaySeconds: delay,
				sendsAt: null,
			};
			if (this.quiet) this.arm();
		});
	}

	/** Voice mode is listening again (or not): the countdown starts once Konvo has said what it prepared. */
	setQuiet(value: boolean): void {
		this.quiet = value;
		if (value) this.arm();
	}

	private arm(): void {
		const draft = this.current;
		if (draft?.phase !== 'counting' || draft.sendsAt !== null) return;
		draft.sendsAt = Date.now() + draft.delaySeconds * 1000;
		this.timer = setTimeout(() => void this.send(), draft.delaySeconds * 1000);
	}

	/** Send it now: the card's own Confirm. */
	async send(): Promise<void> {
		const draft = this.current;
		if (!draft || (draft.phase !== 'counting' && draft.phase !== 'held')) return;
		this.clearTimers();
		draft.phase = 'sending';
		draft.sendsAt = null;
		try {
			const response = await http.post<{embed?: {description?: string}}>(
				Endpoints.KONVO_CARD_ACTION(draft.cardId, 'confirm'),
				{body: {}},
			);
			runInAction(() => {
				const note = response.body?.embed?.description?.trim();
				if (note) draft.note = note;
				this.settle(draft, 'sent');
			});
		} catch {
			runInAction(() => this.settle(draft, 'failed'));
		}
	}

	/** "Wait": stop the countdown; it goes when they say "send it" or press Send. */
	hold(): void {
		const draft = this.current;
		if (draft?.phase !== 'counting') return;
		this.clearTimers();
		draft.phase = 'held';
		draft.sendsAt = null;
	}

	async cancel(): Promise<void> {
		const draft = this.current;
		if (!draft || (draft.phase !== 'counting' && draft.phase !== 'held')) return;
		this.clearTimers();
		this.settle(draft, 'cancelled');
		await http.post(Endpoints.KONVO_CARD_ACTION(draft.cardId, 'cancel'), {body: {}}).catch(() => undefined);
	}

	/** They'd rather write it themselves: drop the card and hand back the text. */
	async takeOver(): Promise<string | null> {
		const draft = this.current;
		if (!draft || (draft.phase !== 'counting' && draft.phase !== 'held')) return null;
		const text = draft.text;
		await this.cancel();
		runInAction(() => {
			if (this.current === draft) this.current = null;
		});
		return text;
	}

	/** Voice mode ended: whatever was counting waits on its card in the Assistant chat instead. */
	reset(): void {
		const draft = this.current;
		if (draft?.phase === 'counting') this.hold();
		this.clearTimers();
		this.current = null;
		this.seen.clear();
	}

	private settle(draft: VoiceDraft, phase: 'sent' | 'cancelled' | 'failed'): void {
		draft.phase = phase;
		draft.sendsAt = null;
		this.clearTimer = setTimeout(
			() => {
				runInAction(() => {
					if (this.current === draft) this.current = null;
				});
			},
			draft.agent ? SETTLED_AGENT_FOR_MS : SETTLED_FOR_MS,
		);
	}

	private clearTimers(): void {
		if (this.timer) clearTimeout(this.timer);
		if (this.clearTimer) clearTimeout(this.clearTimer);
		this.timer = null;
		this.clearTimer = null;
	}
}

const VoiceModeDrafts = new VoiceModeDraftsStore();
export default VoiceModeDrafts;
